/**
 * Minimal typed event emitter. `TEvents` maps event name -> payload type.
 *
 *   interface MyEvents { click: MouseEventArgs; closed: void }
 *   class Thing extends EventEmitter<MyEvents> {}
 *   thing.on("click", (e) => ...)
 */

export type Handler<T> = [T] extends [void] ? () => void : (arg: T) => void;

export class EventEmitter<TEvents extends object> {
  #handlers = new Map<keyof TEvents, Set<(arg: never) => void>>();

  on<K extends keyof TEvents>(event: K, handler: Handler<TEvents[K]>): this {
    let set = this.#handlers.get(event);
    if (!set) {
      set = new Set();
      this.#handlers.set(event, set);
    }
    set.add(handler as (arg: never) => void);
    return this;
  }

  off<K extends keyof TEvents>(event: K, handler: Handler<TEvents[K]>): this {
    this.#handlers.get(event)?.delete(handler as (arg: never) => void);
    return this;
  }

  once<K extends keyof TEvents>(event: K, handler: Handler<TEvents[K]>): this {
    const wrapper = ((arg: TEvents[K]) => {
      this.off(event, wrapper);
      (handler as (a: TEvents[K]) => void)(arg);
    }) as Handler<TEvents[K]>;
    return this.on(event, wrapper);
  }

  /** Number of handlers registered for an event. */
  listenerCount<K extends keyof TEvents>(event: K): number {
    return this.#handlers.get(event)?.size ?? 0;
  }

  removeAllListeners(event?: keyof TEvents): this {
    if (event === undefined) this.#handlers.clear();
    else this.#handlers.delete(event);
    return this;
  }

  /**
   * Invoke every handler for `event`. Iterates a copy so handlers may add or
   * remove listeners during dispatch. A throwing handler is reported to the
   * console and does not stop the remaining handlers.
   */
  emit<K extends keyof TEvents>(
    event: K,
    ...args: [TEvents[K]] extends [void] ? [] : [TEvents[K]]
  ): boolean {
    const set = this.#handlers.get(event);
    if (!set || set.size === 0) return false;
    const arg = args[0] as never;
    for (const handler of [...set]) {
      try {
        handler(arg);
      } catch (err) {
        console.error(`webwin: error in "${String(event)}" handler`, err);
      }
    }
    return true;
  }
}
